import { useEffect, useRef, useState } from 'react';

import delve from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import LoadPrepaidCardModal from 'components/load-prepaid-card-modal';
import StoreConfirmationModal from 'components/store-confirmation-modal';
import { CartPaymentCardType } from 'generated/rbi-graphql';
import { useCommitOrder } from 'hooks/commit-order';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { getNonce } from 'remote/api/first-data';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
// @todo this is normally not good, because we should read state from the context,
// but this context is coupled with the order context so we cant really use it effectively
// the way it's implemented
import { useLogger } from 'state/logger';
import { OrderStatus, useOrderContext } from 'state/order';
import { DeliveryStatus } from 'state/order/types';
import { usePaymentContext } from 'state/payment';
import {
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  IPaymentMethod,
} from 'state/payment/types';
import useRewards from 'state/rewards/use-rewards';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { ENABLE_TH_DIRECT_PAY_FOR_DELIVERY } from 'utils/constants';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import {
  checkIfValidTip,
  extractPaymentTypeForLD,
  getCCFormErrors,
  getFraudPreventionValues,
  initialErrorState,
  initialPaymentState,
  shouldReVault,
  testCardPaymentState,
} from 'utils/payment';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode, isCatering } from 'utils/service-mode';

import { useHandleCommitError } from '../../hooks/use-handle-commit-error';
import { getOrderFailureMessageKey } from '../get-error-dialog-options';
import { CommitOrderPayload } from '../types';

import { UseOrderPayment } from './types';

enum PaymentMode {
  ADDING_NEW_CARD,
  SELECTING_EXISTING_CARD,
}

/**
 * Used to prevent duplication between one-step-checkout and
 * order-payment. This can be removed once TWO_STEP_CHECKOUT flag is cleaned up
 */
export const useOrderPayment = ({ serverOrder, defaultPaymentMethod }: UseOrderPayment) => {
  const { formatMessage } = useIntl();

  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const { email, isRestaurantAvailable, openOrderingUnavailableDialog, store } = useStoreContext();
  const enableDirectPayFlag = useFlag(LaunchDarklyFlag.ENABLE_TH_DIRECT_PAY);
  // The POS for TH needs to have the Card Type in order to use Apple Pay
  const { supportsCardNetwork } = useApplePay({});
  const enableThDirectPayApplePay =
    useFlag(LaunchDarklyFlag.ENABLE_TH_DIRECT_PAY_APPLE_PAY) && supportsCardNetwork;
  const enableThDirectPayGooglePay = useFlag(LaunchDarklyFlag.ENABLE_TH_DIRECT_PAY_GOOGLE_PAY);
  const auth = useAuthContext();
  const order = useOrderContext();
  const { serviceMode } = useServiceModeContext();
  const navigate = useNavigate();
  const { updateCheckoutSelections } = useLDContext();

  /**
   * To enable TH direct pay, the TH direct pay constant is always set on/true
   * the store needs a Chase Merchant ID
   * and if it's a delivery order, the enableThDirectPayForDelivery constant must be on/true
   */
  const currentServiceModeIsEnableForDirectPay =
    serviceMode !== ServiceMode.DELIVERY || ENABLE_TH_DIRECT_PAY_FOR_DELIVERY;
  const enableDirectPay =
    enableDirectPayFlag && !!store.chaseMerchantId && currentServiceModeIsEnableForDirectPay;

  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);
  const enableDoubleVaultingFromPaymentMethods = useFlag(
    LaunchDarklyFlag.ENABLE_DOUBLE_VAULTING_FROM_PAYMENT_METHODS
  );

  const {
    addPaymentMethod,
    checkoutPaymentMethodId,
    getEncryptionDetails,
    getBalanceFromPaymentMethods,
    getPrepaidPaymentMethod,
    loading: paymentLoading,
    openTransferBalanceDialog,
    paymentMethods,
    prepaidReloadPaymentMethodId: reloadPaymentMethodId,
    reloadPrepaidCard,
    transformPaymentValues,
    setCheckoutPaymentMethodId,
    setPrepaidReloadPaymentMethodId: setReloadPaymentMethodId,
  } = usePaymentContext();
  const { reloadPaymentMethods, selectedReloadPaymentMethod } = usePrepaidsPaymentMethods();
  const logger = useLogger();
  const { reloadAmount } = useRewards({
    prepaidCard: getPrepaidPaymentMethod(),
    serverOrder,
    order,
    user: auth.user,
  });
  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const [ServiceModeUnavailableErrorDialog, openServiceModeUnavailableErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Service Mode Unavailable',
    onConfirm: () => navigate(routes.storeLocator),
  });
  const billingCountry = delve(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const userDetailsName = auth.user?.details.name;
  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({ billingCountry, userDetailsName })
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(initialErrorState);
  const [needReloadAgain, setNeedReloadAgain] = useState(false);
  const [commitOrderAfterPricing, setCommitOrderAfterPricing] = useState(false);
  const isCateringOrder = isCatering(serviceMode);
  const [prepaidMethodSelected, setPrepaidMethodSelected] = useState(false);

  const {
    formatCurrencyForLocale,
    shouldConfirmStoreOnCheckout,
    confirmStoreOnCheckout,
  } = useUIContext();

  const [
    StoreConfirmationDialog,
    openStoreConfirmationModal,
    ,
    dismissStoreConfirmationDialog,
  ] = useDialogModal({
    Component: StoreConfirmationModal,
    showCancel: true,
  });

  const {
    commitOrder,
    commitResult: { called: commitOrderCalled, error: commitError, loading: isCommitOrderLoading },
    forceCommitOrder,
    success: commitOrderSuccess,
  } = useCommitOrder({
    rbiOrderId: serverOrder && serverOrder.rbiOrderId,
  });

  // hold on to the original orderId
  const prevOrderId = useRef<string | null>(null);
  useEffect(() => {
    if (serverOrder && !prevOrderId.current) {
      prevOrderId.current = serverOrder.rbiOrderId;
    }
  }, [serverOrder]);

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const checkoutPaymentMethod = paymentMethods.find(
    ({ accountIdentifier, fdAccountId }) =>
      (accountIdentifier ?? fdAccountId) === checkoutPaymentMethodId
  );

  /**
   * Get the payment method used while getting the error message
   * This should be a TimCard or Direct Payment
   * TH should not show the Digital Wallet payment error because it
   * mentions releasing funds, and funds loaded onto the TH card aren't going to refunded
   * if committing the order fails
   */
  const paymentMethodIdUsed = checkoutPaymentMethod?.prepaid
    ? prepaidPaymentMethod?.fdAccountId ?? prepaidPaymentMethod?.accountIdentifier ?? ''
    : checkoutPaymentMethodId;

  const { handleCommitError } = useHandleCommitError({
    errorMsgId: getOrderFailureMessageKey(paymentMethodIdUsed, commitError),
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError,
  });

  const isDelivery = order.serviceMode === ServiceMode.DELIVERY;
  const isDeliveryLoading =
    isDelivery && serverOrder?.delivery?.status === DeliveryStatus.QUOTE_REQUESTED;
  const isDeliveryQuoted =
    isDelivery && serverOrder?.delivery?.status === DeliveryStatus.QUOTE_SUCCESSFUL;

  const isOrderPriceLoading = !serverOrder || serverOrder.status === OrderStatus.PRICE_REQUESTED;
  const isLoading =
    loading ||
    isOrderPriceLoading ||
    paymentLoading ||
    isCommitOrderLoading ||
    isDeliveryLoading ||
    (commitOrderCalled && !commitOrderSuccess && !commitError);
  const isCurbside = order.serviceMode === ServiceMode.CURBSIDE;

  const [LoadPrepaidCardDialog, openLoadPrepaidCardDialog] = useDialogModal<
    {},
    {
      loadAmount: number;
      isLoading?: boolean;
    }
  >({
    Component: LoadPrepaidCardModal,
  });

  useEffectOnce(() => {
    // Add test card to improve QA experience
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ paymentFieldVariations }));
    }
    // Set fire order in 1h
    if (isCurbside || isDelivery) {
      order.setFireOrderIn(3600);
    }
  });

  /**
   * Allowed payment types are dependent on the service mode
   * Catering: no prepaid cards allowed
   * Non-catering: only prepaid cards allowed
   */

  // Temporary check for direct pay ld flag
  const allowDirectPay = (method: IPaymentMethod) => enableDirectPay || !!method.prepaid;

  /**
   * If the payment method selected is the TimCard, charge & reload it if necessary
   * otherwise, if the user is using Direct Pay, commit the order like on BK/PLK but adding the chaseProfileId
   */
  const reloadGiftCardOrUseDirectCheckout = (data?: IApplePayDetails | IGooglePayDetails) => {
    // Check if the checkout method is the prepaid card, reload the card
    if (prepaidMethodSelected) {
      return reloadGiftCardAndCheckout(data);
    }
    // handle apple pay. Only apple data has paymentData field
    if (data && 'paymentData' in data) {
      // If the user use Apple Pay, send the applelePayDetails, otherwise send the googlePayDetails
      const commitInput = {
        ...(isApplePay(checkoutPaymentMethodId)
          ? {
              applePayDetails: data as IApplePayDetails,
            }
          : {
              googlePayDetails: data as IGooglePayDetails,
            }),
        creditType: checkoutPaymentMethod?.credit?.cardType,
        fdAccountId: checkoutPaymentMethodId,
        rbiOrderId: serverOrder?.rbiOrderId,
        storeEmail: email,
        order,
      };

      return commitOrder(commitInput);
    }
    // Otherwise, the user is using Direct Pay
    // Build the commit order input

    // If the user is registered with Chase
    if (checkoutPaymentMethod?.chaseProfileId) {
      const commitInput = {
        creditType: checkoutPaymentMethod?.credit?.cardType,
        fdAccountId: checkoutPaymentMethodId,
        rbiOrderId: serverOrder?.rbiOrderId,
        storeEmail: email,
        chaseProfileId: checkoutPaymentMethod?.chaseProfileId,
        order,
      };

      return commitOrder(commitInput);
    }

    // If the user is registered with Orbital
    if (checkoutPaymentMethod?.orbitalIdentifier) {
      const commitInput = {
        creditType: checkoutPaymentMethod?.credit?.cardType,
        fireOrderIn: order.fireOrderIn,
        rbiOrderId: serverOrder?.rbiOrderId,
        fdAccountId: checkoutPaymentMethod?.fdAccountId,
        payment: {
          orbitalInput: {
            accountIdentifier: checkoutPaymentMethod?.orbitalIdentifier,
          },
          fullName: auth?.user?.details?.name ?? '',
        },
        storeEmail: email,
        order,
      };

      return commitOrder(commitInput);
    }
    // This should never happens but I added it in case it happens so we could track the issue
    return logger.error({
      message: 'TH Direct Pay: The user try to commit order with a CC without chaseProfileId',
    });
  };

  const allowedPaymentMethods = paymentMethods.filter(method => allowDirectPay(method));

  const handleApplePay = ({ data, error }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      // Reload Tim Card or make a direct pay order
      reloadGiftCardOrUseDirectCheckout(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
    setLoading(false);
  };

  // chase apple pay checkout
  const {
    requestingPayment: isApplePayLoading,
    formatApplePayCartPayload,
    requestApplePayPayment,
  } = useApplePay({
    order,
    serverOrder,
    handleApplePay,
  });

  // FD apple pay reload
  const {
    requestingPayment: prepaidIsApplePayLoading,
    requestApplePayPayment: prepaidRequestApplePayPayment,
  } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestingPayment: isGooglePayLoading, requestGooglePayPayment } = useGooglePay({
    onError: () => {
      setLoading(false);
    },
    onCompleted: (data: IGooglePayDetails) => {
      if (!data) {
        setLoading(false);
        return;
      }
      // Reload Tim Card or make a direct pay order
      reloadGiftCardOrUseDirectCheckout(data);
    },
  });

  const hasReloadPaymentMethods = reloadPaymentMethods.length || selectedReloadPaymentMethod;

  const showReloadPaymentMethods =
    !!prepaidPaymentMethod && hasReloadPaymentMethods && reloadAmount > 0 && prepaidMethodSelected;

  const reloadWithApplePay = showReloadPaymentMethods && isApplePay(reloadPaymentMethodId);

  const reloadWithGooglePay = showReloadPaymentMethods && isGooglePay(reloadPaymentMethodId);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }
    if (commitOrderSuccess) {
      navigate(`${routes.orderConfirmation}/${serverOrder.rbiOrderId}`, {
        state: { showToast: true },
      });
    }
    if (serverOrder.status === OrderStatus.PRICE_SUCCESSFUL && commitOrderAfterPricing) {
      // If the orderId hasn't changed, it has not finished pricing as we get a new orderId when its done
      if (prevOrderId.current === serverOrder?.rbiOrderId || (isDelivery && !isDeliveryQuoted)) {
        return;
      }
      handleOrderCommit();
    }
  }, [
    commitOrderSuccess,
    serverOrder,
    prevOrderId,
    isDelivery,
    isDeliveryQuoted,
    commitOrderAfterPricing,
  ]);

  const needsReloadPaymentMethod = !!prepaidPaymentMethod && !hasReloadPaymentMethods;
  const showNewReloadMethod = reloadAmount > 0 && needsReloadPaymentMethod;

  const mode: PaymentMode =
    allowedPaymentMethods.length > 0
      ? PaymentMode.SELECTING_EXISTING_CARD
      : PaymentMode.ADDING_NEW_CARD;

  // when using apple pay, reloadMethod is empty as that value is coming from BE where Apple Pay is not stored
  const isDefaultPaymentMethodNative =
    !!defaultPaymentMethod &&
    (defaultPaymentMethod === CardTypes.APPLE_PAY || defaultPaymentMethod === CardTypes.GOOGLE_PAY);

  const reloadIsValid =
    !reloadAmount ||
    reloadWithApplePay ||
    reloadWithGooglePay ||
    !!selectedReloadPaymentMethod ||
    isDefaultPaymentMethodNative;

  const isValidTip = checkIfValidTip({
    tipAmount: order?.tipAmount,
    subTotalCents: serverOrder?.cart?.subTotalCents,
  });

  const paymentOptions = allowedPaymentMethods.filter(method => {
    const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
    // Needs to do the double check otherwise, if the apple pay flag is enabled, it won't go to the next if statement
    if (!enableThDirectPayApplePay && isApplePay(accountIdentifier)) {
      return false;
    }
    if (!enableThDirectPayGooglePay && isGooglePay(accountIdentifier)) {
      return false;
    }
    // If it's not a native payment blocked by flag, it's going through the dropdown
    return true;
  });

  const checkoutPaymentMethodNeedRevault =
    enableDirectPay &&
    shouldReVault({
      accountIdentifier: checkoutPaymentMethodId,
      enableDoubleVaultingFromPaymentMethods,
      methods: paymentOptions,
    });

  const focusElementWithError = (name: string) => {
    // TODO: Remove queryselector and replace with ref's
    const element: HTMLElement | null = document.querySelector(`[name=${name}]`);
    if (element) {
      element.focus();
    }
  };
  const validateCreditCardForm = () => {
    const { formErrors, hasErrors } = getCCFormErrors(
      paymentValues,
      errors,
      formatMessage,
      paymentFieldVariations,
      billingCountry
    );

    if (!hasErrors || mode !== PaymentMode.ADDING_NEW_CARD) {
      return true;
    }

    const nameOfFirstError = [
      'nameOnCard',
      'cardNumber',
      'cvv',
      'expiry',
      'billingStreetAddress',
      'billingApt',
      'billingCity',
      'billingState',
      'billingZip',
      'billingCountry',
    ].find(name => formErrors[name]);

    if (nameOfFirstError) {
      focusElementWithError(nameOfFirstError);
    }

    setErrors(prevState => ({ ...prevState, ...formErrors }));
    return false;
  };

  const maybeAddNewCard = (): Promise<string | void> => {
    if (
      !reloadWithApplePay &&
      !reloadWithGooglePay &&
      paymentValues.saveCard &&
      mode === PaymentMode.ADDING_NEW_CARD
    ) {
      return addPaymentMethod(transformPaymentValues({ paymentValues, paymentFieldVariations }));
    } else {
      return Promise.resolve();
    }
  };

  const getPrepaidBalance = (methods: IPaymentMethod[]) => {
    const prepaidAccount = methods.find((method: IPaymentMethod) => Boolean(method.prepaid));
    return getBalanceFromPaymentMethods(prepaidAccount!);
  };

  const maybeReloadGiftCard = (input?: IApplePayDetails | IGooglePayDetails): Promise<number> => {
    setNeedReloadAgain(false);

    const shouldReloadGiftCard =
      (input || selectedReloadPaymentMethod) && reloadAmount > 0 && prepaidMethodSelected;

    if (!shouldReloadGiftCard) {
      return Promise.resolve(getPrepaidBalance(paymentMethods));
    }

    const ccReloadDetails = {
      fdAccountId:
        selectedReloadPaymentMethod?.accountIdentifier || selectedReloadPaymentMethod?.fdAccountId,
    };

    const prepaidBalance = getPrepaidBalance(paymentMethods);

    let args = {
      countryCode,
      fundValue: reloadAmount,
      prepaidBalance,
      prepaidFdAccountId:
        prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '',
    };

    switch (reloadPaymentMethodId) {
      case CardTypes.APPLE_PAY:
        args = { ...args, ...{ applePayDetails: input } };
        break;
      case CardTypes.GOOGLE_PAY:
        args = { ...args, ...{ googlePayDetails: input } };
        break;
      default:
        args = { ...args, ...ccReloadDetails };
    }

    // return original balance if reload failed
    return reloadPrepaidCard(args).then(updatedBalance => updatedBalance ?? prepaidBalance);
  };

  const buildCommitOrderPayload = async (prepaidBalance: number): Promise<CommitOrderPayload> => {
    if (!serverOrder) {
      return Promise.reject('Required parameters are not defined.');
    }

    if (prepaidBalance < serverOrder.cart.totalCents) {
      setNeedReloadAgain(true);
      return Promise.reject('Insufficient prepaid balance.');
    }

    const { rbiOrderId } = serverOrder;

    if (prepaidMethodSelected) {
      return Promise.resolve({
        fdAccountId:
          prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '',
        order,
        rbiOrderId,
        storeEmail: email,
      });
    }

    if (!allowDirectPay) {
      return Promise.reject('No valid payment methods found.');
    }

    if (reloadWithApplePay) {
      return Promise.resolve({
        order,
        rbiOrderId,
        storeEmail: email,
      });
    }
    if (reloadWithGooglePay) {
      return Promise.resolve({
        order,
        rbiOrderId,
        storeEmail: email,
      });
    }

    if (checkoutPaymentMethod) {
      return Promise.resolve({
        creditType: checkoutPaymentMethod.credit
          ? ((checkoutPaymentMethod.credit.cardType as unknown) as CartPaymentCardType)
          : null,
        fdAccountId:
          checkoutPaymentMethod.fdAccountId ?? checkoutPaymentMethod.accountIdentifier ?? '',
        order,
        rbiOrderId,
        storeEmail: email,
      });
    }

    // Otherwise we have to jump through hoops to get more values from First Data
    const {
      fdAccessToken,
      fdApiKey,
      fdCustomerId,
      fdPublicKey,
      algorithm,
    } = await getEncryptionDetails();

    const reshapedPaymentValues = transformPaymentValues({
      paymentValues,
      paymentFieldVariations,
    });
    const nonceResponse = await getNonce(
      reshapedPaymentValues,
      fdPublicKey,
      fdApiKey,
      fdAccessToken,
      fdCustomerId,
      auth.user?.details.isoCountryCode as ISOs | undefined,
      onlySendPostalCode,
      algorithm
    );
    const nonce = await nonceResponse.json();

    const fraudPreventionValues = getFraudPreventionValues(paymentValues);

    return {
      billingAddress: fraudPreventionValues.billingAddress,
      ccBin: fraudPreventionValues.ccBin,
      ccExpiry: fraudPreventionValues.expiry ?? '',
      ccLast4: fraudPreventionValues.ccLast4,
      creditType: paymentValues.cardType as CartPaymentCardType,
      fdAccessToken,
      fdNonce: nonce.token.tokenId,
      fullName: fraudPreventionValues.fullName,
      order,
      rbiOrderId,
      storeEmail: email,
    };
  };

  const reloadGiftCardAndCheckout = async (data?: IApplePayDetails | IGooglePayDetails) => {
    // should never happen, since user cannot submit without
    // successfully pricing an order
    if (!serverOrder) {
      throw new Error('unreachable');
    }

    try {
      await maybeAddNewCard();
      // reloads prepaid if necessary
      const currentPrepaidBalance = await maybeReloadGiftCard(data);

      // commit order if prepaid balance is sufficient
      const commitOrderPayload = await buildCommitOrderPayload(currentPrepaidBalance);
      await commitOrder(commitOrderPayload);
    } catch (e) {
      logger.error(`Error reloading giftcard: ${e}`);
      handleCommitError(e);
    } finally {
      setLoading(false);
    }
  };

  const placeOrder = async () => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);

    const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);

    if (!isRestaurantAvailableForOrdering) {
      openOrderingUnavailableDialog();
      return;
    }

    const checkoutWithApplePay = isApplePay(checkoutPaymentMethodId);
    const checkoutWithGooglePay = isGooglePay(checkoutPaymentMethodId);
    if (checkoutWithApplePay) {
      // Apple pay need a specific summary format (item list, qty, taxes)
      const applePayPayload = await formatApplePayCartPayload();
      return requestApplePayPayment(applePayPayload);
    }
    const totalWithTip = serverOrder?.cart?.totalCents; // totalCents includes the tip amount as well
    // Google doesn't request a detailled summary
    const nativePayParams = {
      countryCode,
      currencyCode,
      total: checkoutWithApplePay || checkoutWithGooglePay ? totalWithTip : reloadAmount,
    };
    if (reloadWithApplePay) {
      return prepaidRequestApplePayPayment(nativePayParams);
    }
    if (checkoutWithGooglePay || reloadWithGooglePay) {
      // Get the right prepaid or payment gateway configs
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: checkoutWithGooglePay ? TransactionType.PAYMENT : TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        ...nativePayParams,
        gatewayMerchantId,
        gateway,
      });
    }
    const ccFormIsValid = validateCreditCardForm();
    if (!ccFormIsValid) {
      return;
    }
    // Check wether the user is using it's TimCard or Direct Pay
    return reloadGiftCardOrUseDirectCheckout();
  };

  const handleOrderCommit = async () => {
    setCommitOrderAfterPricing(false);
    setLoading(true);
    try {
      if (!serverOrder) {
        throw new Error('Unable to get the rbiOrderId from serverOrder.');
      }
      await placeOrder();
    } catch (e) {
      handleCommitError(e);
    } finally {
      setLoading(false);
    }
  };

  const placeOrderAriaLabel = serviceMode
    ? formatMessage(
        { id: 'placeOrderDescription' },
        {
          serviceMode: getLocalizedServiceMode(serviceMode, formatMessage),
          price: formatCurrencyForLocale(serverOrder?.cart.totalCents ?? 0),
        }
      )
    : undefined;

  useEffect(() => {
    // Make sure that the checkout payment method is always part of the paymentOptions
    if (
      !paymentOptions.some(method => {
        const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
        return accountIdentifier === checkoutPaymentMethodId;
      })
    ) {
      setCheckoutPaymentMethodId(
        paymentOptions[0]?.accountIdentifier ?? paymentOptions[0]?.fdAccountId ?? ''
      );
    }
  }, [checkoutPaymentMethodId, paymentOptions, setCheckoutPaymentMethodId]);

  useEffect(() => {
    if (!enableDirectPay && !checkoutPaymentMethod?.prepaid) {
      // Set the prepaid card as checkout method if direct pay is not selected
      setCheckoutPaymentMethodId(
        prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? ''
      );
      setPrepaidMethodSelected(true);
    } else if (checkoutPaymentMethod?.prepaid) {
      setPrepaidMethodSelected(true);
    } else {
      setPrepaidMethodSelected(false);
    }
  }, [
    checkoutPaymentMethod,
    checkoutPaymentMethodId,
    enableDirectPay,
    prepaidPaymentMethod,
    setCheckoutPaymentMethodId,
    setReloadPaymentMethodId,
  ]);

  useEffect(() => {
    const ldPaymentType = extractPaymentTypeForLD(checkoutPaymentMethod);
    updateCheckoutSelections(ldPaymentType);
  }, [checkoutPaymentMethod, updateCheckoutSelections]);

  const shouldCommitOrder =
    serviceMode === ServiceMode.DELIVERY || serviceMode === ServiceMode.DRIVE_THRU;

  return {
    isCateringOrder,
    openServiceModeUnavailableErrorDialog,
    setCommitOrderAfterPricing,
    checkoutPaymentMethod,
    confirmStoreOnCheckout,
    openStoreConfirmationModal,
    handleOrderCommit,
    isLoading,
    isApplePayLoading,
    prepaidIsApplePayLoading,
    isGooglePayLoading,
    enableDirectPay,
    prepaidPaymentMethod,
    reloadIsValid,
    checkoutPaymentMethodNeedRevault,
    isValidTip,
    paymentOptions,
    setCheckoutPaymentMethodId,
    checkoutPaymentMethodId,
    needReloadAgain,
    openLoadPrepaidCardDialog,
    openTransferBalanceDialog,
    showNewReloadMethod,
    showReloadPaymentMethods,
    prepaidMethodSelected,
    reloadAmount,
    focusElementWithError,
    shouldCommitOrder,
    placeOrderAriaLabel,
    LoadPrepaidCardDialog,
    ErrorDialog,
    StoreConfirmationDialog,
    ServiceModeUnavailableErrorDialog,
  };
};
